/**
 * Footer Component
 * 
 * Compact, mobile-optimized footer
 * Features:
 * - Thin, minimal design
 * - Fully responsive layout
 * - Optimized for all screen sizes
 * - Dark theme with hover effects
 * 
 * @component
 * @returns {React.ReactElement} Compact footer
 */
import React from "react";
import { FaShieldAlt, FaGlobeAmericas, FaLock, FaExclamationTriangle } from "react-icons/fa";

const Footer = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="w-full bg-gray-900 text-gray-300 border-t border-gray-800/40 py-3 md:py-4">
      {/* Mobile Layout */}
      <div className="md:hidden px-3 sm:px-4">
        <div className="space-y-3">
          {/* Top Row: Left and Right */}
          <div className="flex justify-between items-center text-xs">
            {/* Left side - SMS & GIS */}
            <div className="flex gap-3">
              <a href="#" className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1">
                <FaShieldAlt className="w-3.5 h-3.5" />
                <span>SMS</span>
              </a>
              <span className="text-gray-600">•</span>
              <a href="https://globalintegrationsystem.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1">
                <FaGlobeAmericas className="w-3.5 h-3.5" />
                <span>GIS</span>
              </a>
            </div>

            {/* Right side - GCRD */}
            <a href="https://www.gcrdhub.com/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1">
              <span>GCRD</span>
              <FaShieldAlt className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Second Row: Privacy Left, Slavery Right */}
          <div className="flex justify-between items-center text-xs">
            <a href="https://www.gcrdhub.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1">
              <FaLock className="w-3.5 h-3.5" />
              <span>Privacy</span>
            </a>
            
            <a href="https://www.gcrdhub.com/modern-slavery-statement-and-policy/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-blue-400 transition-colors flex items-center gap-1">
              <span>Slavery Policy</span>
              <FaExclamationTriangle className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Bottom: Copyright */}
          <div className="text-center text-xs text-gray-500 border-t border-gray-700 pt-2">
            <p>© {currentYear} ERSM - GCRD. All rights reserved.</p>
          </div>
        </div>
      </div>

      {/* Desktop Layout - Truly Left and Right */}
      <div className="hidden md:flex items-center text-xs px-4 sm:px-8">
        {/* Left section - Copyright and branding */}
        <div className="flex items-center gap-3 whitespace-nowrap">
          <FaShieldAlt className="w-4 h-4 text-gray-400" />
          <p className="text-gray-400">© {currentYear} ERSM - GCRD</p>
        </div>
        
        {/* Right section - All links pushed to far right */}
        <div className="flex gap-4 text-gray-400 ml-auto">
          <a href="#" className="hover:text-blue-400 transition-colors flex items-center gap-2 group">
            <FaShieldAlt className="w-4 h-4 group-hover:scale-110 transition-transform" />
            <span>SMS - GCRD</span>
          </a>
          <span className="text-gray-600">•</span>
          <a href="https://globalintegrationsystem.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-2 group">
            <FaGlobeAmericas className="w-4 h-4 group-hover:scale-110 transition-transform" />
            <span>GIS</span>
          </a>
          <span className="text-gray-600">•</span>
          <a href="https://www.gcrdhub.com/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-2 group">
            <span>GCRD</span>
            <FaShieldAlt className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </a>
          <span className="text-gray-600">•</span>
          <a href="https://www.gcrdhub.com/privacy-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-2 group">
            <FaLock className="w-4 h-4 group-hover:scale-110 transition-transform" />
            <span>Privacy Policy</span>
          </a>
          <span className="text-gray-600">•</span>
          <a href="https://www.gcrdhub.com/modern-slavery-statement-and-policy/" target="_blank" rel="noopener noreferrer" className="hover:text-blue-400 transition-colors flex items-center gap-2 group">
            <span>Modern Slavery</span>
            <FaExclamationTriangle className="w-4 h-4 group-hover:scale-110 transition-transform" />
          </a>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
